import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import ScaleLoader from "react-spinners/ScaleLoader";

// ✅ Module-level tracking
const globalProcessingOrders = new Map(); // orderId -> { promise, result }

export const MockOrderConfirmation = () => {
  const [loading, setLoading] = useState(true);
  const [orderData, setOrderData] = useState(null);
  const [error, setError] = useState(null);
  const [orderId, setOrderId] = useState("");
  const [logs, setLogs] = useState([]);

  const location = useLocation();
  const componentMounted = useRef(true);

  // Generate UUID helper
  const generateUUID = () => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  };

  // Helper to add logs
  const addLog = (message, type = "info") => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs((prev) => [...prev, { timestamp, message, type }]);
    console.log(`[${timestamp}] ${message}`);
  };

  // Extract order reference from URL
  const getOrderReference = () => {
    const params = new URLSearchParams(location.search);
    return params.get("ref");
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      componentMounted.current = false;
    };
  }, []);

  // First useEffect - Extract and validate orderId
  useEffect(() => {
    addLog("🔍 Checking URL parameters...");

    const orderRef = getOrderReference();

    if (!orderRef) {
      addLog("❌ No order reference in URL", "error");
      setError("Order reference not found in URL");
      setLoading(false);
      return;
    }

    if (orderRef.length !== 36) {
      addLog("❌ Invalid order reference format", "error");
      setError("Invalid order reference format (must be 36 characters)");
      setLoading(false);
      return;
    }

    addLog(
      `✅ Valid order reference found: ${orderRef.slice(-6).toUpperCase()}`
    );
    setOrderId(orderRef);
  }, []);

  // Second useEffect - Call API with shared promise
  useEffect(() => {
    const confirmOrder = async () => {
      if (!orderId || orderId === "") {
        return;
      }

      // ✅ Check if another instance is already processing this order
      if (globalProcessingOrders.has(orderId)) {
        const existing = globalProcessingOrders.get(orderId);

        addLog(
          "🔒 Order already being processed, waiting for result...",
          "warning"
        );

        try {
          const result = await existing.promise;

          if (!componentMounted.current) return;

          addLog("✅ Received shared result from other request", "success");

          if (result.success) {
            addLog("✅ Order confirmed successfully!", "success");

            // ✅ Add cached flag if present
            if (result.cached) {
              addLog(
                `⚡ Result was cached (processed ${result.processedAgo} ago)`,
                "info"
              );
            }

            setOrderData(result);
          } else {
            addLog(`❌ Order confirmation failed: ${result.message}`, "error");
            setError(result.message);
          }

          setLoading(false);
        } catch (err) {
          if (!componentMounted.current) return;

          addLog(`❌ Error: ${err.message}`, "error");
          setError(err.message);
          setLoading(false);
        }

        return;
      }

      // ✅ This is the first instance - make the API call
      addLog("🔓 Acquired processing lock", "info");

      const apiPromise = (async () => {
        try {
          addLog("🚀 Sending order confirmation request...");
          addLog(`   Order ID: ${orderId.slice(-6).toUpperCase()}`);

          const response = await axios.get(
            `${process.env.REACT_APP_ZULAL_API_URL}/api/v2/mock/orders/confirm?ref=${orderId}`
          );

          addLog("✅ Received response from server");

          const result = response.data;

          if (result.success) {
            addLog("✅ Order confirmed successfully!", "success");

            if (result.cached) {
              addLog(
                `⚡ Result was cached (processed ${result.processedAgo} ago)`,
                "info"
              );
            }

            // ✅ Ensure all required fields exist (for cached responses)
            if (
              !result.orderTotal &&
              result.message === "Order already confirmed"
            ) {
              addLog(
                "⚠️ Cached response missing details, using defaults",
                "warning"
              );
              result.orderTotal = 0;
              result.paymentState = "CACHED";
              result.deliveryLocation = "N/A";
              result.customerCode = "N/A";
            }
          } else {
            addLog(`❌ Order confirmation failed: ${result.message}`, "error");
          }

          return result;
        } catch (err) {
          addLog(`❌ Error: ${err.message}`, "error");
          console.error("Full error:", err);
          throw err;
        } finally {
          addLog("🔓 Released processing lock", "info");

          setTimeout(() => {
            globalProcessingOrders.delete(orderId);
          }, 5000);
        }
      })();

      globalProcessingOrders.set(orderId, { promise: apiPromise });

      try {
        const result = await apiPromise;

        if (!componentMounted.current) return;

        if (result.success) {
          setOrderData(result);
        } else {
          setError(result.message || "Failed to confirm order");
        }
      } catch (err) {
        if (!componentMounted.current) return;

        console.error("❌ Error confirming mock order:", err);
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to confirm order"
        );
      } finally {
        if (componentMounted.current) {
          setLoading(false);
        }
      }
    };

    confirmOrder();
  }, [orderId]);

  const getShortOrderRef = (ref) => {
    return ref ? ref.slice(-6).toUpperCase() : "N/A";
  };

  const getLogColor = (type) => {
    switch (type) {
      case "success":
        return "text-green-400";
      case "error":
        return "text-red-400";
      case "warning":
        return "text-yellow-400";
      default:
        return "text-gray-300";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center mb-8">
              <ScaleLoader color="#36d7b7" height={40} loading={loading} />
              <p className="mt-4 text-lg text-gray-600">
                Processing mock order...
              </p>
            </div>

            <div className="bg-gray-900 text-gray-100 rounded-lg p-4 font-mono text-sm max-h-96 overflow-y-auto">
              <div className="font-semibold mb-2 text-green-400">
                📋 Request Logs:
              </div>
              {logs.map((log, index) => (
                <div key={index} className="mb-1">
                  <span className="text-gray-500">[{log.timestamp}]</span>{" "}
                  <span className={getLogColor(log.type)}>{log.message}</span>
                </div>
              ))}
              {logs.length === 0 && (
                <div className="text-gray-500">Waiting for logs...</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !orderData?.success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">❌</div>
              <h1 className="text-2xl font-bold text-red-600 mb-4">
                Order Not Confirmed
              </h1>
              <p className="text-gray-600 mb-6">
                {error || "Unable to confirm order"}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
              >
                Try Again
              </button>
            </div>

            <div className="mt-8">
              <div className="bg-gray-900 text-gray-100 rounded-lg p-4 font-mono text-xs max-h-64 overflow-y-auto">
                <div className="font-semibold mb-2 text-red-400">
                  📋 Error Logs:
                </div>
                {logs.map((log, index) => (
                  <div key={index} className="mb-1">
                    <span className="text-gray-500">[{log.timestamp}]</span>{" "}
                    <span className={getLogColor(log.type)}>{log.message}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">✅</div>
            <h1 className="text-3xl font-bold text-green-600 mb-2">
              Mock Order Confirmed!
            </h1>
            <p className="text-gray-600">All steps executed successfully</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <span className="mr-2">📦</span> Order Details
              </h2>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Order Ref:</span>
                  <span className="font-mono font-semibold">
                    #{getShortOrderRef(orderData.orderReference)}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Total:</span>
                  <span className="font-semibold text-green-600">
                    AED {orderData.orderTotal?.toFixed(2)}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className="font-semibold text-green-600">
                    {orderData.paymentState}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Duration:</span>
                  <span className="font-semibold">{orderData.duration}</span>
                </div>

                {orderData.cached && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                    <div className="text-sm text-blue-800">
                      ⚡ <strong>Cached Response</strong>
                      <br />
                      Processed {orderData.processedAgo} ago
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="font-semibold text-blue-900 mb-4 flex items-center">
                <span className="mr-2">✉️</span> Notifications Sent
              </h3>
              <ul className="space-y-2 text-sm text-blue-800">
                <li className="flex items-start">
                  <span className="mr-2">✅</span>
                  <span>SMS confirmation sent to customer</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✅</span>
                  <span>Email confirmation sent to customer</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✅</span>
                  <span>Email notification sent to call center</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="font-semibold mb-2 flex items-center">
              <span className="mr-2">📋</span> Request Logs
            </h3>
            <div className="bg-gray-900 text-gray-100 rounded-lg p-4 font-mono text-xs max-h-96 overflow-y-auto">
              {logs.map((log, index) => (
                <div key={index} className="mb-1">
                  <span className="text-gray-500">[{log.timestamp}]</span>{" "}
                  <span className={getLogColor(log.type)}>{log.message}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition"
            >
              🔄 Test Duplicate Request
            </button>

            <button
              onClick={() => {
                const newOrderId = generateUUID();
                window.location.href = `/mock-orderconfirmation?ref=${newOrderId}`;
              }}
              className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 transition"
            >
              🆕 Test New Order
            </button>
          </div>

          <div className="mt-6 text-center text-sm text-gray-500">
            <p>🧪 This is a mock order confirmation for testing purposes</p>
          </div>
        </div>
      </div>
    </div>
  );
};
